/**
 * Policy Rules Editor Component
 * Form-based editor for XACML policy rules
 */

import { useMemo } from 'react'
import { Plus, Trash2, GripVertical, MessageSquare, Filter, EyeOff, ChevronDown } from 'lucide-react'

import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ObligationEditor, createPrefilledObligation } from './obligation-editors'
import type { ObligationType } from './obligation-editors'
import { AttributeCombobox } from './attribute-combobox'
import { ValueCombobox } from './value-combobox'
import { DATA_TYPE_TO_XACML } from './constants'
import { useAllAttributes } from '@/hooks/use-attributes'
import type { Attribute, ValidationRules } from '@/types/attribute'
import type {
  ParsedXacmlRule,
  ParsedXacmlCondition,
  ParsedXacmlObligation,
  RuleEffect,
} from '@/types/policy'

/**
 * Props for PolicyRulesEditor
 */
interface PolicyRulesEditorProps {
  rules: ParsedXacmlRule[]
  onChange: (rules: ParsedXacmlRule[]) => void
  className?: string
}

/**
 * Available condition functions
 */
const CONDITION_FUNCTIONS = [
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-equal', label: 'String Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-contains', label: 'String Contains' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-starts-with', label: 'String Starts With' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-regexp-match', label: 'Regex Match' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:integer-equal', label: 'Integer Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:integer-greater-than', label: 'Integer Greater Than' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:integer-less-than', label: 'Integer Less Than' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:boolean-equal', label: 'Boolean Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:date-equal', label: 'Date Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:time-equal', label: 'Time Equal' },
]

/**
 * Available data types for conditions
 */
const DATA_TYPES = [
  { value: 'http://www.w3.org/2001/XMLSchema#string', label: 'String' },
  { value: 'http://www.w3.org/2001/XMLSchema#boolean', label: 'Boolean' },
  { value: 'http://www.w3.org/2001/XMLSchema#integer', label: 'Integer' },
  { value: 'http://www.w3.org/2001/XMLSchema#double', label: 'Double' },
  { value: 'http://www.w3.org/2001/XMLSchema#date', label: 'Date' },
  { value: 'http://www.w3.org/2001/XMLSchema#time', label: 'Time' },
]

/**
 * Get badge variant for rule effect
 */
function getEffectBadgeVariant(effect: RuleEffect): 'default' | 'destructive' {
  return effect === 'Permit' ? 'default' : 'destructive'
}

/**
 * Single condition editor
 */
function ConditionEditor({
  condition,
  onChange,
  onRemove,
}: {
  condition: ParsedXacmlCondition
  onChange: (condition: ParsedXacmlCondition) => void
  onRemove: () => void
}) {
  const { data: attributesData } = useAllAttributes()
  const allAttributes = attributesData?.data ?? []

  const selectedAttribute = useMemo(
    () => allAttributes.find((a) => a.key === condition.attributeId),
    [allAttributes, condition.attributeId]
  )

  const enumValues = useMemo(() => {
    const rules = selectedAttribute?.validationRules as ValidationRules | null
    return rules?.enumValues
  }, [selectedAttribute])

  const handleAttributeSelect = (key: string, attr: Attribute | undefined) => {
    const updates: Partial<ParsedXacmlCondition> = { attributeId: key }
    if (attr) {
      updates.dataType = DATA_TYPE_TO_XACML[attr.dataType] || condition.dataType
    }
    onChange({ ...condition, ...updates })
  }

  return (
    <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
      <div className="flex-1 grid gap-3 sm:grid-cols-[1fr_1fr_auto_1fr_1fr]">
        {/* Attribute ID */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Attribute</Label>
          <AttributeCombobox
            value={condition.attributeId}
            onChange={handleAttributeSelect}
          />
        </div>

        {/* Function */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Function</Label>
          <Select
            value={condition.functionId}
            onValueChange={(value) =>
              onChange({ ...condition, functionId: value })
            }
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {CONDITION_FUNCTIONS.map((fn) => (
                <SelectItem key={fn.value} value={fn.value}>
                  {fn.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* NOT toggle */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">NOT</Label>
          <div className="flex items-center justify-center h-8">
            <Switch
              checked={condition.negated ?? false}
              onCheckedChange={(checked) => onChange({ ...condition, negated: checked })}
              className="scale-75"
            />
          </div>
        </div>

        {/* Value */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Value</Label>
          <ValueCombobox
            value={String(condition.value)}
            onChange={(val) => onChange({ ...condition, value: val })}
            enumValues={enumValues}
            placeholder="Value"
          />
        </div>

        {/* Data Type */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Data Type</Label>
          <Select
            value={condition.dataType}
            onValueChange={(value) =>
              onChange({ ...condition, dataType: value })
            }
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * Single rule editor
 */
function RuleEditor({
  rule,
  index,
  onChange,
  onRemove,
}: {
  rule: ParsedXacmlRule
  index: number
  onChange: (rule: ParsedXacmlRule) => void
  onRemove: () => void
}) {
  const handleAddCondition = () => {
    const newCondition: ParsedXacmlCondition = {
      attributeId: '',
      functionId: 'urn:oasis:names:tc:xacml:1.0:function:string-equal',
      negated: false,
      value: '',
      dataType: 'http://www.w3.org/2001/XMLSchema#string',
    }
    onChange({
      ...rule,
      conditions: [...rule.conditions, newCondition],
      rawCondition: undefined,
    })
  }

  const handleConditionChange = (condIndex: number, condition: ParsedXacmlCondition) => {
    const newConditions = [...rule.conditions]
    newConditions[condIndex] = condition
    onChange({ ...rule, conditions: newConditions, rawCondition: undefined })
  }

  const handleRemoveCondition = (condIndex: number) => {
    onChange({
      ...rule,
      conditions: rule.conditions.filter((_, i) => i !== condIndex),
      rawCondition: undefined,
    })
  }

  const handleAddTypedObligation = (type: ObligationType) => {
    const newObligation = createPrefilledObligation(type, rule.effect)
    onChange({
      ...rule,
      obligations: [...(rule.obligations ?? []), newObligation],
      // Clear raw snapshot so serializer uses the edited obligations
      // (same pattern as condition edits clearing rawCondition).
      rawObligationExpressions: undefined,
    })
  }

  const handleObligationChange = (oblIndex: number, obligation: ParsedXacmlObligation) => {
    const updated = [...(rule.obligations ?? [])]
    updated[oblIndex] = obligation
    onChange({
      ...rule,
      obligations: updated,
      rawObligationExpressions: undefined,
    })
  }

  const handleRemoveObligation = (oblIndex: number) => {
    onChange({
      ...rule,
      obligations: (rule.obligations ?? []).filter((_, i) => i !== oblIndex),
      rawObligationExpressions: undefined,
    })
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Rule Header */}
      <div className="flex items-center gap-2 p-4 border-b">
        <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
        <span className="text-sm font-medium text-muted-foreground">
          Rule {index + 1}
        </span>
        <Badge variant={getEffectBadgeVariant(rule.effect)} className="ml-2">
          {rule.effect}
        </Badge>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Rule Content */}
      <div className="p-4 space-y-4">
        {/* Basic Fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Rule ID */}
          <div className="space-y-2">
            <Label htmlFor={`rule-id-${index}`}>Rule ID</Label>
            <Input
              id={`rule-id-${index}`}
              value={rule.ruleId}
              onChange={(e) => onChange({ ...rule, ruleId: e.target.value })}
              placeholder="e.g., allow-read-access"
              className="font-mono"
            />
          </div>

          {/* Effect */}
          <div className="space-y-2">
            <Label>Effect</Label>
            <Select
              value={rule.effect}
              onValueChange={(value: RuleEffect) =>
                onChange({ ...rule, effect: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Permit">
                  <span className="flex items-center gap-2">
                    <Badge variant="default" className="px-1.5 py-0">Permit</Badge>
                    <span className="text-muted-foreground">Allow access</span>
                  </span>
                </SelectItem>
                <SelectItem value="Deny">
                  <span className="flex items-center gap-2">
                    <Badge variant="destructive" className="px-1.5 py-0">Deny</Badge>
                    <span className="text-muted-foreground">Deny access</span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor={`rule-desc-${index}`}>Description (optional)</Label>
          <Textarea
            id={`rule-desc-${index}`}
            value={rule.description ?? ''}
            onChange={(e) =>
              onChange({ ...rule, description: e.target.value || undefined })
            }
            placeholder="Describe what this rule does..."
            rows={2}
          />
        </div>

        <Separator />

        {/* Conditions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Conditions (optional)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCondition}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Condition
            </Button>
          </div>

          {rule.conditions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-3 rounded-md border border-dashed text-center">
              No conditions. This rule applies unconditionally when the policy target matches.
            </p>
          ) : (
            <div className="space-y-2">
              {rule.conditions.map((condition, condIndex) => (
                <ConditionEditor
                  key={condIndex}
                  condition={condition}
                  onChange={(c) => handleConditionChange(condIndex, c)}
                  onRemove={() => handleRemoveCondition(condIndex)}
                />
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Obligations */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Obligations (optional)</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Obligation
                  <ChevronDown className="ml-2 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleAddTypedObligation('message')}>
                  <MessageSquare className="mr-2 h-4 w-4 text-blue-500" />
                  Message
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddTypedObligation('row-filter')}>
                  <Filter className="mr-2 h-4 w-4 text-amber-500" />
                  Row Filter
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddTypedObligation('mask')}>
                  <EyeOff className="mr-2 h-4 w-4 text-purple-500" />
                  Mask Column
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {(rule.obligations ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-3 rounded-md border border-dashed text-center">
              No obligations. Add obligations to instruct the PEP on how to enforce the decision (e.g., column masking).
            </p>
          ) : (
            <div className="space-y-2">
              {(rule.obligations ?? []).map((obligation, oblIndex) => (
                <ObligationEditor
                  key={oblIndex}
                  obligation={obligation}
                  onChange={(o) => handleObligationChange(oblIndex, o)}
                  onRemove={() => handleRemoveObligation(oblIndex)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Main Policy Rules Editor component
 */
export function PolicyRulesEditor({
  rules,
  onChange,
  className,
}: PolicyRulesEditorProps) {
  const handleAddRule = () => {
    const newRule: ParsedXacmlRule = {
      ruleId: `rule-${rules.length + 1}`,
      effect: 'Permit',
      description: '',
      conditions: [],
      obligations: [],
    }
    onChange([...rules, newRule])
  }

  const handleRuleChange = (index: number, rule: ParsedXacmlRule) => {
    const newRules = [...rules]
    newRules[index] = rule
    onChange(newRules)
  }

  const handleRemoveRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index))
  }

  // Count permit and deny rules
  const permitCount = rules.filter((r) => r.effect === 'Permit').length
  const denyCount = rules.filter((r) => r.effect === 'Deny').length

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Policy Rules</CardTitle>
          <div className="flex items-center gap-2">
            {rules.length > 0 && (
              <>
                <Badge variant="secondary">
                  {rules.length} rule{rules.length !== 1 ? 's' : ''}
                </Badge>
                {permitCount > 0 && (
                  <Badge variant="default" className="text-xs">
                    {permitCount} Permit
                  </Badge>
                )}
                {denyCount > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {denyCount} Deny
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Define the rules that determine access decisions. Rules are evaluated in order.
        </p>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              No rules defined. Add at least one rule to determine access decisions.
            </p>
            <Button type="button" variant="outline" onClick={handleAddRule}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Rule
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              {rules.map((rule, index) => (
                <RuleEditor
                  key={index}
                  rule={rule}
                  index={index}
                  onChange={(r) => handleRuleChange(index, r)}
                  onRemove={() => handleRemoveRule(index)}
                />
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleAddRule}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Rule
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Create an empty rule
 */
export function createEmptyRule(): ParsedXacmlRule {
  return {
    ruleId: 'new-rule',
    effect: 'Permit',
    description: '',
    conditions: [],
    obligations: [],
  }
}

export default PolicyRulesEditor
